import { TicketIcon, XIcon } from '@phosphor-icons/react';
import { useState } from 'react';

import { Chip, type ChipIntent } from '@/components/common/chip';
import { Timeline } from '@/components/common/timeline';
import { Drawer } from '@/components/feedback/drawer';
import { ImageLightbox } from '@/components/feedback/image-lightbox';
import { ACTIVITY_CHIP, CLAIM_STATUS_CHIP, WARRANTY_REASONS } from '@/features/requests';

import { DrawerSkeleton } from '../../components/drawer-skeleton';
import { unitLabel } from '../../replacements/format';
import { formatTimelineStamp } from '../../replacements/timeline';
import { useOrderWarrantyClaim } from '../hooks';
import { warrantyTimelineEvents } from '../timeline';
import type { OrderWarrantyClaimDetail } from '../types';

import styles from './warranty-preview-drawer.module.scss';

/** AC-shaped failure copy, mirroring the listing drawer's ("…your warranty claims…"). */
const LOAD_ERROR = "We couldn't load this warranty claim. Please try again.";

export type WarrantyPreviewDrawerProps = {
  orderId: string;
  claimId: string;
  open: boolean;
  onClose: () => void;
};

/**
 * The shared four-status timeline colourway (AC 112445: imported, never restated), widened to
 * accept any wire token because `activity[].status` parses as a bare string — an unrecognised one
 * degrades to a neutral chip instead of crashing on `undefined.intent`.
 *
 * **Read inside this function body, never at module scope.** `@/features/requests` re-exports
 * `WarrantyFilterDrawer` — which imports the `@/features/orders` barrel — *before* it re-exports
 * `./warranty/format`, so a module-construction-time read of `ACTIVITY_CHIP` from an
 * orders-reachable module sees an unassigned binding and throws.
 */
function activityChip(status: string): { label: string; intent: ChipIntent } {
  const chips: Record<string, { label: string; intent: ChipIntent }> = ACTIVITY_CHIP;
  return chips[status] ?? { label: status, intent: 'neutral' };
}

function HeaderTitle({ status }: { status?: OrderWarrantyClaimDetail['status'] }) {
  const chip = status ? CLAIM_STATUS_CHIP[status] : undefined;
  return (
    <span className={styles.header}>
      <span className={styles.iconBadge}>
        <TicketIcon size={20} weight="fill" aria-hidden />
      </span>
      <span className={styles.headerHeading}>Ticket Preview</span>
      {chip ? <Chip intent={chip.intent}>{chip.label}</Chip> : null}
    </span>
  );
}

/**
 * Read-only Ticket Preview for one order-scoped Warranty Claim (112445). A deliberate sibling of
 * `orders/replacements`' `ReplacementPreviewDrawer`, not a generalisation of it — ADR-0023 rules
 * that a *third* order-scoped request surface is the signal to extract.
 *
 * Nothing here can be acted on: approving and rejecting live in the Requests → Warranty queue, and
 * Approved and Rejected are terminal. Closing pops back to the Warranty Claims listing underneath.
 *
 * One section is absent *permanently*: Approved renders no replacement-order banner and no "View
 * Order", which AC 112445 forbids outright and `CONTEXT.md` confirms independently ("Approving a
 * claim creates no Order and no Installation"). Figma `327:285100` draws one; it is a lift from the
 * replacement Approved state.
 */
export function WarrantyPreviewDrawer({
  orderId,
  claimId,
  open,
  onClose,
}: WarrantyPreviewDrawerProps) {
  const { data: detail, isPending, isError } = useOrderWarrantyClaim(orderId, claimId, open);
  const [preview, setPreview] = useState<string | null>(null);
  /** An evidence image whose presign failed arrives with `url: null` — unshowable and
   *  unpreviewable, so it is dropped rather than rendered as a broken thumbnail that opens the
   *  lightbox on nothing (ADO 119316's defect class, found on the replacement sibling). Narrowed
   *  here so the map below keeps a plain `string`. */
  /** A `url` that parses but the browser still can't fetch — an expired presign, a bad signature —
   *  is the same "unshowable" case ADO 119316 covers for a `null` one, just discovered later, at
   *  `<img>` load time instead of parse time (ADO 119323). Tracked by id so it drops from the grid
   *  the same way a url-less image never enters it, rather than sitting there as a broken-image
   *  icon with a lightbox button that opens on nothing. */
  const [unloadable, setUnloadable] = useState<ReadonlySet<string>>(new Set());
  const images = (detail?.images ?? []).flatMap((image) =>
    image.url && !unloadable.has(image.id) ? [{ ...image, url: image.url }] : [],
  );
  const dropUnloadable = (id: string) =>
    setUnloadable((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        title={<HeaderTitle status={detail?.status} />}
        hideFooter
      >
        <div className={styles.body}>
          {isPending ? <DrawerSkeleton label="Loading claim details" /> : null}
          {isError ? <p className={styles.notice}>{LOAD_ERROR}</p> : null}
          {detail ? (
            <>
              <div className={styles.section}>
                <span className={styles.name}>
                  {unitLabel(detail.itemName, detail.quantity, detail.unitIndex)}
                </span>
                {/* No `?? bundleName` fallback — BE populates `category` for bundle components as of
                    112445 (2026-08-04). The listing row renders this identically, and one claim must
                    not read differently on the two surfaces. */}
                <span className={styles.category}>{detail.category ?? '—'}</span>
              </div>

              {/* No visible "Device Images" heading: Figma draws none and neither does the shipped
                  sibling — ADR-0023 reads the AC's name for it as naming the region. `imageUrl`,
                  the Device's catalogue photograph, is parsed and rendered nowhere; the thumbnails
                  are the uploaded evidence in `images[]`. */}
              {images.length ? (
                <ul className={styles.thumbGrid}>
                  {images.map((image, index) => (
                    <li key={image.id}>
                      <button
                        type="button"
                        className={styles.thumbBtn}
                        aria-label={`View image ${index + 1}`}
                        onClick={() => setPreview(image.url)}
                      >
                        <img
                          className={styles.thumbImg}
                          src={image.url}
                          alt=""
                          onError={() => dropUnloadable(image.id)}
                        />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}

              <div className={styles.reasonCard}>
                {/* The client enum map, never the wire's `reasonLabel` (which the schema does not
                    parse) and never Figma's off-enum "Device Broken" filler — ADR-0023, inherited
                    from 0022, so one casing stays ours. */}
                <h3 className={styles.reasonHeading}>{WARRANTY_REASONS[detail.reason]}</h3>
                <p className={styles.description}>{detail.description}</p>
              </div>

              <h3 className={styles.timelineHeading}>Activity Timeline</h3>
              <Timeline
                items={warrantyTimelineEvents(detail).map((event) => {
                  const chip = activityChip(event.status);
                  return {
                    // Uniform grey dot per Figma — 'pending' maps the dot to --border-default.
                    status: 'pending' as const,
                    title: <Chip intent={chip.intent}>{chip.label}</Chip>,
                    // The comma stamp both frames draw — not `formatWarrantyDateTime`, whose pipe
                    // belongs to the queue drawer's "Order Date & Time" field.
                    timestamp: formatTimelineStamp(event.at),
                  };
                })}
              />

              {/* Rejected only, below the timeline (AC 112445); no reason on the wire → no
                  section, never an empty box. Copy is the AC's, not Figma's — see the SCSS. */}
              {detail.status === 'rejected' && detail.rejectionReason ? (
                <div className={styles.rejection} role="alert">
                  <span className={styles.rejectionHeading}>
                    <XIcon size={16} aria-hidden />
                    Rejection Reason
                  </span>
                  <p className={styles.rejectionValue}>{detail.rejectionReason}</p>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </Drawer>

      {preview ? (
        <ImageLightbox
          src={preview}
          label="Device image preview"
          onClose={() => setPreview(null)}
        />
      ) : null}
    </>
  );
}
